import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import {
  CustomExclusionColumn,
  CustomExclusionItem,
  LINUX_SCAN_TARGET_PATH,
  MALWARE_SCAN_ID,
  MALWARE_SCAN_MISCELLANEOUS,
  MalwareScan,
  MalwareScanAction,
  MalwareScanArchives,
  MalwareScanArchivesSettings,
  MalwareScanCpuUsageControl,
  MalwareScanCredentials,
  MalwareScanForm,
  MalwareScanMiscellaneous,
  ScanExclusion,
  ScanTargetColumn,
  ScanTargetFilesItem,
  WINDOWS_SCAN_TARGET_PATH,
} from '../types';
import { FormGroupValue } from '../../../../../../ui/gzForm/formGroup/types/form-group-value';
import {
  DataTableStrings,
  DefaultColumnKeys,
  IColumnType,
  IDataTableColumn,
} from '../../../../../../ui/gzDataTable/types';
import { ChipsGeneratorStrings } from '../../../../../../ui/gzChipsGeneratorInput/types';
import { getFormattedDateForTask } from '../../../../../networkAppContainer/helpers/utils';

export class MalwareScanHelper {
  public static getScanTypeOptions(shouldShowNetworkType?: boolean): Array<SelectOption> {
    const allScanTypes = [
      {
        title: translateFn('Quick scan'),
        value: Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_QUICK,
      },
      {
        title: translateFn('Full scan'),
        value: Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_FULL,
      },
      {
        title: translateFn('Memory scan'),
        value: Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY,
      },
      {
        title: translateFn('Network scan'),
        value: Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK,
      },
      {
        title: translateFn('Custom scan'),
        value: Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM,
      },
    ];

    if (shouldShowNetworkType) {
      return allScanTypes;
    }

    const networkIndex = allScanTypes.findIndex((element, index) => {
      if (element.value === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK) {
        return index;
      }
    });

    // remove network scan from selector
    allScanTypes.splice(networkIndex, 1);
    return allScanTypes;
  }

  public static getFileTypeOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('All files'),
        value: Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.ALL_FILES,
      },
      {
        title: translateFn('Just applications'),
        value: Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.JUST_APPLICATIONS,
      },
      {
        title: translateFn('User-defined extensions'),
        value: Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.CUSTOM_EXTENTIONS,
      },
    ];
  }

  public static getMaxDepthOptions(): Array<SelectOption> {
    return [
      {
        title: '2',
        value: 2,
      },
      {
        title: '4',
        value: 4,
      },
      {
        title: '6',
        value: 6,
      },
      {
        title: '8',
        value: 8,
      },
      {
        title: '10',
        value: 10,
      },
      {
        title: '12',
        value: 12,
      },
      {
        title: '14',
        value: 14,
      },
      {
        title: '16',
        value: 16,
      },
    ];
  }

  public static getInfectedFileActionOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Report only'),
        value: Constants.Tasks.Endpoints.SCAN_INFECTED_ACTIONS.REPORT_ONLY,
      },
      {
        title: translateFn('Remediate'),
        value: Constants.Tasks.Endpoints.SCAN_INFECTED_ACTIONS.REMEDIATE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: Constants.Tasks.Endpoints.SCAN_INFECTED_ACTIONS.MOVE_TO_QUARANTINE,
      },
    ];
  }

  public static getRootkitsActionOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Report only'),
        value: Constants.Tasks.Endpoints.SCAN_ROOT_KIT_ACTIONS.REPORT_ONLY,
      },
      {
        title: translateFn('Remediate'),
        value: Constants.Tasks.Endpoints.SCAN_ROOT_KIT_ACTIONS.REMEDIATE,
      },
    ];
  }

  public static getPuaActionOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Report only'),
        value: Constants.Tasks.Endpoints.SCAN_PUA_ACTIONS.REPORT_ONLY,
      },
      {
        title: translateFn('Remediate'),
        value: Constants.Tasks.Endpoints.SCAN_PUA_ACTIONS.REMEDIATE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: Constants.Tasks.Endpoints.SCAN_PUA_ACTIONS.MOVE_TO_QUARANTINE,
      },
    ];
  }

  public static getMaximumUsageOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Low'),
        value: Constants.Tasks.Endpoints.SCAN_CPU_PERCENTAGE.LOW,
      },
      {
        title: translateFn('Medium'),
        value: Constants.Tasks.Endpoints.SCAN_CPU_PERCENTAGE.MEDIUM,
      },
      {
        title: translateFn('High'),
        value: Constants.Tasks.Endpoints.SCAN_CPU_PERCENTAGE.HIGH,
      },
    ];
  }

  public static getDefaultFileType(): number {
    return Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.ALL_FILES;
  }

  public static getFileTypeFromScanOption(scanOption: number): number {
    if (scanOption === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE) {
      return Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.JUST_APPLICATIONS;
    }

    return Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.ALL_FILES;
  }

  public static getScanTitleFromType(type: number): string {
    const malwareScanTypes = MalwareScanHelper.getScanTypeOptions(true);
    const selectedType = malwareScanTypes.find(scan => scan.value === type)?.title || '';
    return selectedType + ' ' + getFormattedDateForTask();
  }

  public static getDefaultOptionFromType(type: number): number {
    if (
      type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM ||
      type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
    ) {
      return Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_NORMAL;
    }

    return Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_CUSTOM;
  }

  public static getScanInsideArchiveFromType(type: number): boolean {
    return (
      type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM ||
      type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
    );
  }

  public static getLimitArchiveSizeFromType(type: number): number {
    if (type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY) {
      return 16;
    }

    return 10;
  }

  public static getMaxArchiveDepthFromType(type: number): number {
    switch (type) {
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_QUICK:
        return 2;
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_FULL:
        return 2;
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY:
        return 4;
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM:
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK:
      default:
        return 16;
    }
  }

  public static getScanEmailArchivesFromType(type: number): boolean {
    const returnTrueFor = [
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_FULL,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK,
    ];
    return returnTrueFor.includes(type);
  }

  public static getMiscellaneousFromType(type: number): Array<MALWARE_SCAN_MISCELLANEOUS> {
    switch (type) {
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_QUICK:
        return [
          MALWARE_SCAN_MISCELLANEOUS.BOOT_SECTORS,
          MALWARE_SCAN_MISCELLANEOUS.UEFI,
          MALWARE_SCAN_MISCELLANEOUS.ROOTKITS,
          MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES,
          MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
        ];
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_FULL:
        return [
          MALWARE_SCAN_MISCELLANEOUS.BOOT_SECTORS,
          MALWARE_SCAN_MISCELLANEOUS.UEFI,
          MALWARE_SCAN_MISCELLANEOUS.REGISTRY,
          MALWARE_SCAN_MISCELLANEOUS.ROOTKITS,
          MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS,
          MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES,
          MALWARE_SCAN_MISCELLANEOUS.MEMORY,
          MALWARE_SCAN_MISCELLANEOUS.COOKIES,
          MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES,
          MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
        ];
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY:
        return [MALWARE_SCAN_MISCELLANEOUS.MEMORY];
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM:
        return [
          MALWARE_SCAN_MISCELLANEOUS.ROOTKITS,
          MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS,
          MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES,
          MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES,
          MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
        ];
      case Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK:
        return [
          MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS,
          MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES,
          MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES,
          MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
        ];
    }

    throw new Error('Could not find type of ' + type);
  }

  public static getMiscellaneousFromScanOption(scanOption: number): Array<MALWARE_SCAN_MISCELLANEOUS> {
    if (scanOption === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_AGRESSIVE) {
      return [
        MALWARE_SCAN_MISCELLANEOUS.BOOT_SECTORS,
        MALWARE_SCAN_MISCELLANEOUS.UEFI,
        MALWARE_SCAN_MISCELLANEOUS.REGISTRY,
        MALWARE_SCAN_MISCELLANEOUS.ROOTKITS,
        MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS,
        MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES,
        MALWARE_SCAN_MISCELLANEOUS.MEMORY,
        MALWARE_SCAN_MISCELLANEOUS.COOKIES,
        MALWARE_SCAN_MISCELLANEOUS.DETACHABLE_VOLUMES,
        MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
      ];
    }

    if (scanOption === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_NORMAL) {
      return [
        MALWARE_SCAN_MISCELLANEOUS.ROOTKITS,
        MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS,
        MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES,
        MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES,
        MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
      ];
    }

    if (scanOption === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE) {
      return [
        MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS,
        MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES,
        MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME,
      ];
    }

    throw Error('Function should not be called with custom option.');
  }

  public static getActionForInfectedFilesFromType(type: number): number {
    if (type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY) {
      return Constants.Tasks.Endpoints.SCAN_INFECTED_ACTIONS.REPORT_ONLY;
    }

    return Constants.Tasks.Endpoints.SCAN_INFECTED_ACTIONS.REMEDIATE;
  }

  public static getDefaultValueMaximumCpuUsageControl(): string {
    return Constants.Tasks.Endpoints.SCAN_CPU_PERCENTAGE.HIGH;
  }

  public static getDefaultActionForPua(): number {
    return Constants.Tasks.Endpoints.SCAN_PUA_ACTIONS.REPORT_ONLY;
  }

  public static getRootkitActionFromTypeAndScanOption(type: number, option: number | undefined): number {
    if (type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY) {
      return Constants.Tasks.Endpoints.SCAN_ROOT_KIT_ACTIONS.REPORT_ONLY;
    }

    if (
      type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM &&
      option === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE
    ) {
      return Constants.Tasks.Endpoints.SCAN_ROOT_KIT_ACTIONS.REPORT_ONLY;
    }

    if (
      type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK &&
      option === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE
    ) {
      return Constants.Tasks.Endpoints.SCAN_ROOT_KIT_ACTIONS.REPORT_ONLY;
    }

    return Constants.Tasks.Endpoints.SCAN_ROOT_KIT_ACTIONS.REMEDIATE;
  }

  public static getDefaultScanTargetPathFromType(type: number): Array<ScanTargetFilesItem> {
    if (type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_QUICK) {
      return [
        { [ScanTargetColumn.PATH]: WINDOWS_SCAN_TARGET_PATH.WIN_DIR_SYSTEM32 },
        { [ScanTargetColumn.PATH]: WINDOWS_SCAN_TARGET_PATH.TEMP },
        { [ScanTargetColumn.PATH]: LINUX_SCAN_TARGET_PATH.LIB },
        { [ScanTargetColumn.PATH]: LINUX_SCAN_TARGET_PATH.ETC },
      ];
    }

    if (type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_FULL) {
      return [{ [ScanTargetColumn.PATH]: WINDOWS_SCAN_TARGET_PATH.LOCAL_DRIVERS }];
    }

    return [];
  }

  public static getDefaultExclusionFrom(): number {
    return Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_PROFILE.USE_GLOBAL;
  }

  public static getExclusionTypeSelectOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('File'),
        value: Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FILE,
      },
      {
        title: translateFn('Folder'),
        value: Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FOLDER,
      },
      {
        title: translateFn('Extension'),
        value: Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.EXTENSION,
      },
    ];
  }

  public static getTranslatedExclusionFromType(type: number): string {
    const allSelectOptions = this.getExclusionTypeSelectOptions();
    const result = allSelectOptions.find(selectOption => selectOption.value === type);
    if (!result) {
      throw Error('Could not find Exclusion of type ' + type);
    }
    return result.title;
  }

  public static getArchiveSettingsFromScanOption(option: number): FormGroupValue {
    const response = {
      enable: true,
      scanEmail: true,
      settings: {
        limitArchiveSize: 10,
        maximumArchiveDepth: 16,
      },
    };

    switch (option) {
      case Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_AGRESSIVE:
      case Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_NORMAL:
        return response;
      case Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE:
        response.enable = false;
        response.settings.maximumArchiveDepth = 2;
        return response;
    }

    throw Error('Function should not be called with custom option.');
  }

  public static getScanTargetColumns(): Array<IDataTableColumn> {
    return [
      {
        key: ScanTargetColumn.PATH,
        header: translateFn('Files and folders to scan'),
        columnType: IColumnType.TEXT,
      },
      {
        key: DefaultColumnKeys.ACTIONS,
        header: translateFn('Actions'),
        columnType: IColumnType.DISPLAY,
      },
    ];
  }

  public static getCommonStringsForDataTable() {
    return {
      editRowTooltip: translateFn('Edit'),
      deleteRowTooltip: translateFn('Delete'),
      validateEditTooltip: translateFn('Save#preserve state'),
      cancelEditTooltip: translateFn('Cancel'),
      extraValidationToastMessage: translateFn('Duplicate entry found. Unable to save changes.'),
      requiredFieldMessage: translateFn('Required field'),
      disabledEditTooltip: translateFn('Validation failed due to errors.'),
      copyConfirmationTooltip: translateFn('Copied'),
      resetPriorityButton: translateFn('Reset priority'),
      textInputPlaceholder: '',
      dropdownApplyButtonString: '',
      dropdownAllItemsLabel: '',
    };
  }

  public static getStringsForTargetTable(): DataTableStrings {
    return {
      ...this.getCommonStringsForDataTable(),
      emptyStateFooterButton: translateFn('Add path'),
    };
  }

  public static getStringsForExclusionTable(): DataTableStrings {
    return {
      ...this.getCommonStringsForDataTable(),
      emptyStateFooterButton: translateFn('Add exclusion'),
    };
  }

  public static getCustomExclusionsColumns(): Array<IDataTableColumn> {
    return [
      {
        key: CustomExclusionColumn.TYPE,
        header: translateFn('Exclusion type'),
        columnType: IColumnType.SINGLE_SELECT,
        options: this.getExclusionTypeSelectOptions().map(option => ({ value: option.value, label: option.title })),
      },
      {
        key: CustomExclusionColumn.PATH,
        header: translateFn('Files and folders to scan'),
        columnType: IColumnType.TEXT,
      },
      {
        key: DefaultColumnKeys.ACTIONS,
        header: translateFn('Actions'),
        columnType: IColumnType.DISPLAY,
      },
    ];
  }

  public static getPlaceholderDependingOnCustomExclusionType(type: number | null): string {
    switch (type) {
      case Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FILE:
        return translateFn('Specify the file path');
      case Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FOLDER:
        return translateFn('Specify the folder path');
      case Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.EXTENSION:
        return translateFn('Specify extensions');
      default:
        return translateFn('Specify the path');
    }
  }

  public static getLabelDependingOnCustomExclusionType(type: number | null): string {
    switch (type) {
      case Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.EXTENSION:
        return translateFn('Extension');

      case Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FILE:
      case Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FOLDER:
      default:
        return translateFn('Path');
    }
  }

  public static transformCustomExclusionsPayload(values: Array<CustomExclusionItem>): Array<ScanExclusion> {
    return values.map(item => {
      return {
        path: item[CustomExclusionColumn.PATH],
        exclusionType: Number(item[CustomExclusionColumn.TYPE]),
      };
    });
  }

  public static getStringsForUserDefinedExtensions(): ChipsGeneratorStrings {
    return {
      label: translateFn('Extensions:'),
      placeholder: translateFn('Specify extensions here. Separate them by comma or press Enter.'),
      notUniqueErrorMessage: translateFn('The extension already exists.'),
      hintText: translateFn('Each entry must be unique.'),
    };
  }

  public static isScanEditable(type: number) {
    const editableTypes = [
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK,
    ];
    return editableTypes.includes(type);
  }

  public static addRequiredCharToStringWhenEditable(type: number, str: string): string {
    if (!this.isScanEditable(type)) {
      return str;
    }

    return str + '*';
  }

  public static transformFormValueToConsoleRequestParams(formGroupValue: MalwareScanForm, targets): MalwareScan {
    if (!targets) {
      throw new Error('No targets provided');
    }

    const res: MalwareScan = {
      settings: {
        path: '', // atm no idea what this field is about, it looks like it should always be an empty strings except the memory case below
        taskName: formGroupValue.general.taskName as string,
        type: MALWARE_SCAN_ID,
        targets: targets,
        specifics: {
          scanType: formGroupValue.general.scanType as number,
          scanProfile: formGroupValue.settings.scanOption as number,
          scanSettings: {
            action: {
              ...(formGroupValue.settings.action as MalwareScanAction),
            },
            archives: this.getMalwareScanArchivesFromForm(formGroupValue.settings.archives as FormGroupValue),
            fileTypes: {
              types: formGroupValue.settings.fileTypes as number,
              extentions: formGroupValue.userCustomExtensions,
            },
            miscellaneous: this.getMiscellaneousFromForm(
              formGroupValue.settings.miscellaneous as FormGroupValue,
              formGroupValue.general.runTask
            ),
          },
          scanTarget: {
            targets: formGroupValue.scanTargetFilesAndFolders,
            exclusions: {
              exclusions: formGroupValue.exclusionItems,
              profile: formGroupValue.settings.exclusionProfile as number,
            },
          },
          pauseWhenOnBattery: formGroupValue.general.pauseWhenOnBattery as boolean,
          cpuUsageControl: {
            enabled: (formGroupValue.general.cpuUsageControl as MalwareScanCpuUsageControl).enabled as boolean,
            maximumUsage: (formGroupValue.general.cpuUsageControl as MalwareScanCpuUsageControl).maximumUsage as string,
          },
        },
      },
    };

    if (formGroupValue.general.scanType === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK) {
      res.settings.specifics.scanTarget.credentials = formGroupValue.settings.credentials as MalwareScanCredentials;
    }

    if (formGroupValue.general.scanType === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY) {
      res.settings.exclusionsTaskType = Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FILE;
      res.settings.specifics.scanTarget.exclusions.profile =
        Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_PROFILE.USE_GLOBAL;
      res.settings.path = ['', ''];
    }

    return res;
  }

  public static getMalwareScanArchivesFromForm(formGroupValue: FormGroupValue): MalwareScanArchives {
    const malwareScanArchives: MalwareScanArchives = {
      enable: formGroupValue.enable ? 'on' : false,
      scanEmail: formGroupValue.scanEmail as boolean,
    };

    if (malwareScanArchives.enable === 'on') {
      malwareScanArchives.settings = {
        limitArchiveSize: (formGroupValue.settings as MalwareScanArchivesSettings).limitArchiveSize.toString(),
        maximumArchiveDepth: (formGroupValue.settings as MalwareScanArchivesSettings).maximumArchiveDepth,
      };
    }

    return malwareScanArchives;
  }

  public static getMiscellaneousFromForm(formGroupValue: FormGroupValue, runTaskValue): MalwareScanMiscellaneous {
    return {
      ignoreKeyloggers: formGroupValue.ignoreKeyloggers ? 'on' : false,
      preserveLastAccessTime: formGroupValue.preserveLastAccessTime ? 'on' : false,
      resumeIfInterrupted: formGroupValue.resumeIfInterrupted ? 'on' : false,
      runTask: runTaskValue ? 'on' : false,
      scanBoot: formGroupValue.scanBoot ? 'on' : false,
      scanUefi: formGroupValue.scanUefi ? 'on' : false,
      scanCookie: formGroupValue.scanCookie ? 'on' : false,
      scanDetachableVolumes: formGroupValue.scanDetachableVolumes ? 'on' : false,
      scanMemory: formGroupValue.scanMemory ? 'on' : false,
      scanNetworkFiles: formGroupValue.scanNetworkFiles ? 'on' : false,
      scanNewChanged: formGroupValue.scanNewChanged ? 'on' : false,
      scanPUA: formGroupValue.scanPUA ? 'on' : false,
      scanRegistry: formGroupValue.scanRegistry ? 'on' : false,
      scanRootkits: formGroupValue.scanRootkits ? 'on' : false,
      shutDownWhenFinished: !!formGroupValue.shutDownWhenFinished,
    };
  }
}
